<!--
  Dashboard scaffolding shown on `/` while Snowflake queries hydrate.

  Mirrors the real index.vue layout 1-to-1 — same section breakpoints,
  same KPI count, same chart proportions, same table heights — so the
  page doesn't jump when the actual data lands. Pure CSS pulse via
  Tailwind's `animate-pulse` on the gray placeholder rectangles. No
  query, no spinner; the user sees what's coming, not "loading…".

  Sections (matching index.vue):
    1. System Health & Performance — 4 KPI cards + spend chart (2/3) +
       long-running queries panel (1/3)
    2. Cost Analytics — overview cards + trend chart + warehouse
       breakdown + recommendations + anomalies + heatmap + user table

  When new dashboard sections appear in index.vue, add their skeleton
  shapes here so the layout stays stable. The `bone()` helper just
  returns a Tailwind class string for a consistent gray + pulse —
  centralizes the look so the skeleton stays visually coherent.
-->

<template>
  <div class="space-y-12">
    <!-- ── Section 1 — System Health & Performance ─────────────── -->
    <section class="space-y-6">
      <div class="flex items-center gap-2 mb-4">
        <div class="h-8 w-1 bg-green-500/50 rounded-full" />
        <div :class="bone()" class="h-6 w-64 rounded-md" />
      </div>

      <!-- KPI row — 4 cards. -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div
          v-for="i in 4"
          :key="`kpi-${i}`"
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 space-y-3"
        >
          <div class="flex items-center justify-between">
            <div :class="bone()" class="h-3 w-24 rounded" />
            <div :class="bone()" class="h-8 w-8 rounded-lg" />
          </div>
          <div :class="bone()" class="h-8 w-32 rounded" />
          <div :class="bone()" class="h-3 w-20 rounded" />
        </div>
      </div>

      <!-- Spend chart (2/3) + long-running queries (1/3). -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div class="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div :class="bone()" class="h-5 w-56 rounded mb-6" />
          <div class="flex items-end gap-2 h-48">
            <div
              v-for="i in 12"
              :key="`bar-${i}`"
              :class="bone()"
              class="flex-1 rounded-t"
              :style="{ height: `${30 + ((i * 53) % 70)}%` }"
            />
          </div>
        </div>
        <div class="lg:col-span-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div :class="bone()" class="h-5 w-44 rounded mb-4" />
          <div class="space-y-3">
            <div
              v-for="i in 4"
              :key="`q-${i}`"
              class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600 space-y-1.5"
            >
              <div class="flex justify-between items-center">
                <div :class="bone()" class="h-3 w-20 rounded" />
                <div :class="bone()" class="h-4 w-10 rounded" />
              </div>
              <div :class="bone()" class="h-3 w-32 rounded" />
              <div :class="bone()" class="h-3 w-full rounded" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="border-t border-slate-200 dark:border-slate-700 my-8 opacity-50" />

    <!-- ── Section 2 — Cost Analytics ──────────────────────────── -->
    <section class="space-y-8">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <div class="h-8 w-1 bg-purple-500/50 rounded-full" />
          <div :class="bone()" class="h-7 w-44 rounded-md" />
        </div>
        <div :class="bone()" class="h-4 w-32 rounded" />
      </div>

      <!-- Overview cards row. -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div
          v-for="i in 4"
          :key="`overview-${i}`"
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 space-y-3"
        >
          <div :class="bone()" class="h-3 w-28 rounded" />
          <div :class="bone()" class="h-9 w-36 rounded" />
          <div :class="bone()" class="h-3 w-24 rounded" />
        </div>
      </div>

      <!-- Trend chart + warehouse breakdown — 50/50. -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div :class="bone()" class="h-5 w-40 rounded mb-6" />
          <div :class="bone()" class="h-56 w-full rounded" />
        </div>
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div :class="bone()" class="h-5 w-48 rounded mb-6" />
          <div class="space-y-3">
            <div v-for="i in 5" :key="`wh-${i}`" class="space-y-1.5">
              <div class="flex justify-between">
                <div :class="bone()" class="h-3 w-32 rounded" />
                <div :class="bone()" class="h-3 w-16 rounded" />
              </div>
              <div :class="bone()" class="h-2 w-full rounded" />
            </div>
          </div>
        </div>
      </div>

      <!-- Recommendations + anomalies — 50/50. -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div
          v-for="i in 2"
          :key="`half-${i}`"
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
        >
          <div :class="bone()" class="h-5 w-40 rounded mb-4" />
          <div class="space-y-3">
            <div
              v-for="j in 3"
              :key="`rec-${i}-${j}`"
              class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600 space-y-2"
            >
              <div :class="bone()" class="h-3 w-44 rounded" />
              <div :class="bone()" class="h-3 w-full rounded" />
              <div :class="bone()" class="h-3 w-2/3 rounded" />
            </div>
          </div>
        </div>
      </div>

      <!-- Heatmap. -->
      <div class="space-y-4">
        <div class="flex items-center gap-2">
          <div :class="bone()" class="h-5 w-5 rounded" />
          <div :class="bone()" class="h-5 w-40 rounded" />
        </div>
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div class="grid grid-cols-24 gap-1" style="grid-template-columns: repeat(24, minmax(0, 1fr));">
            <div
              v-for="i in 168"
              :key="`heat-${i}`"
              :class="bone()"
              class="h-4 rounded-sm"
            />
          </div>
        </div>
      </div>

      <!-- User spend table. -->
      <div class="space-y-4">
        <div class="flex items-center gap-2">
          <div :class="bone()" class="h-5 w-5 rounded" />
          <div :class="bone()" class="h-5 w-48 rounded" />
        </div>
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-4">
            <div :class="bone()" class="h-3 w-32 rounded flex-1" />
            <div :class="bone()" class="h-3 w-20 rounded" />
            <div :class="bone()" class="h-3 w-20 rounded" />
            <div :class="bone()" class="h-3 w-20 rounded" />
          </div>
          <div
            v-for="i in 6"
            :key="`row-${i}`"
            class="px-4 py-3 border-b border-slate-100 dark:border-slate-700/50 flex items-center gap-4"
          >
            <div class="flex items-center gap-3 flex-1">
              <div :class="bone()" class="h-7 w-7 rounded-full" />
              <div :class="bone()" class="h-3 w-32 rounded" />
            </div>
            <div :class="bone()" class="h-3 w-16 rounded" />
            <div :class="bone()" class="h-3 w-16 rounded" />
            <div :class="bone()" class="h-3 w-16 rounded" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * Class string for a single skeleton block. Centralized so every
 * placeholder pulses at the same rate / shade, and so swapping the
 * pulse animation (or the placeholder color) later is a one-line
 * change.
 */
function bone() {
  return 'bg-slate-200 dark:bg-slate-700/60 animate-pulse'
}
</script>
